import { Box, Typography, Divider, Link } from '@mui/material';
import LegalPageLayout from '../../components/legal/LegalPageLayout';

const NAVY = '#0a326d';
const TEXT = '#334155';

function Section({ number, title, children }) {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} color={NAVY} sx={{ mb: 1 }}>
        {number}. {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>{children}</Box>
    </Box>
  );
}

function P({ children }) {
  return (
    <Typography variant="body2" sx={{ color: TEXT, lineHeight: 1.7, textAlign: 'justify' }}>
      {children}
    </Typography>
  );
}

export default function DsaLegalInformation() {
  return (
    <LegalPageLayout>
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant="h5"
          fontWeight={800}
          color="#072146"
          sx={{ letterSpacing: 0.3, fontSize: { xs: '1.15rem', sm: '1.4rem', md: '1.5rem' } }}
        >
          DSA Information, Content Moderation, and Abuse Reporting
        </Typography>
        <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>
          Last Updated: May 14, 2026 - TifoXR Platform - Regulation (EU) 2022/2065 - FINAL V4 BOTTOM
        </Typography>
      </Box>

      <Divider sx={{ my: 1 }} />

      <Section number={1} title="Provider Information (Article 12 DSA)">
        <P>Provider: Global Mobile Software LLC (GMS)</P>
        <P>Address: 7014 13th Avenue, Suite 202, Brooklyn, NY 11228, USA</P>
        <P>
          Contact: <Link href="mailto:compliance@globalmobisoft.com" sx={{ fontWeight: 700 }}>compliance@globalmobisoft.com</Link>
        </P>
        <P>
          Website:{' '}
          <Link href="https://www.globalmobisoft.com" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 700 }}>
            https://www.globalmobisoft.com
          </Link>
        </P>
        <P>
          EU Representative: Data Protection Representative Limited (DataRep) - 77 Camden Street Lower, Dublin D02 XE80,
          Ireland
        </P>
        <P>UK Representative: Data Protection Representative Limited - 107-111 Fleet Street, London EC4A 2AB, UK</P>
        <P>
          EU/UK Contact:{' '}
          <Link href="mailto:tifoxr@datarep.com" sx={{ fontWeight: 700 }}>tifoxr@datarep.com</Link>
          {' '}- Portal:{' '}
          <Link href="https://www.datarep.com/tifoxr" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 700 }}>
            https://www.datarep.com/tifoxr
          </Link>
        </P>
        <P>
          Scope: Applies to all TifoXR deployments including immersive.tifoxr.com and all club-specific deployments.
          Single abuse contact for all clubs.
        </P>
      </Section>

      <Section number={2} title="Service and User-Generated Content">
        <P>Hosting service per Article 3(g) DSA.</P>
        <P>
          UGC: text chat (including chants/comments relating to a space), live voice/audio where enabled,
          usernames/avatars/reactions, friend requests.
        </P>
        <P>
          No video/image upload for general users. Club personnel may upload limited content via CMS including
          advertising and sponsored content with images and videos.
        </P>
      </Section>

      <Section number={3} title="Notice and Action (Article 16 DSA)">
        <P>
          Report via: (1) In-App Report button, (2) Email{' '}
          <Link href="mailto:compliance@globalmobisoft.com" sx={{ fontWeight: 700 }}>compliance@globalmobisoft.com</Link>
          {' '}subject DSA Report, (3) EU/UK Rep{' '}
          <Link href="mailto:tifoxr@datarep.com" sx={{ fontWeight: 700 }}>tifoxr@datarep.com</Link>.
        </P>
        <P>
          Include explanation why illegal, location (URL/screenshot/timestamp), name/email (except CSAM), good faith
          statement.
        </P>
        <P>Review within 24h, 7 days/week. Trusted Flaggers prioritized per Article 22.</P>
      </Section>

      <Section number={4} title="Content Moderation (Articles 14, 17 DSA)">
        <P>Basis: Acceptable Use Policy, Terms, applicable law.</P>
        <P>
          Automated moderation: Hive.ai for TEXT CHAT ONLY for toxicity/hate/harassment/self-harm/sexual content.
          Hive.ai prevents abusive messages from being sent.
        </P>
        <P>
          Voice chat: No automated moderation currently deployed; voice is live and not transcribed by AI. Depending
          on future club deployments, additional voice moderation provider may be added.
        </P>
        <P>
          Human review for all Article 16 notices and appeals by trained moderators. No facial recognition /
          biometric identification. No shadow banning.
        </P>
        <P>Measures: removal, warning, temp suspension 24h-30d, permanent ban.</P>
        <P>CSAM zero tolerance reported to NCMEC/law enforcement.</P>
      </Section>

      <Section number={5} title="Statement of Reasons and Appeals (Articles 17, 20, 21 DSA)">
        <P>
          SoR via in-app/email: what restricted, facts, basis (contractual/legal), automated use (Hive.ai text
          only), redress.
        </P>
        <P>
          Appeal within 6 months via reply to SoR or{' '}
          <Link href="mailto:compliance@globalmobisoft.com" sx={{ fontWeight: 700 }}>compliance@globalmobisoft.com</Link>
          {' '}subject DSA Appeal.
        </P>
        <P>Human reviewer not involved originally, within 48h (24h urgent child safety).</P>
        <P>Out-of-court dispute settlement body per Article 21 available, non-binding.</P>
      </Section>

      <Section number={6} title="Recommender Systems and Advertising (Articles 25, 26, 27, 28 DSA)">
        <Typography variant="body2" fontWeight={700} sx={{ color: NAVY }}>
          Recommender Systems Article 27:
        </Typography>
        <P>
          Chat is displayed chronologically. Latest message typed appears at bottom so users can read easier without
          scrolling back up.
        </P>
        <P>Rolling history window - older messages scroll up as new messages arrive at bottom.</P>
        <P>
          No personalized ranking, no profiling, no algorithmic recommendation for UGC. Latest wins at bottom. Voice
          is live and not ranked.
        </P>

        <Typography variant="body2" fontWeight={700} sx={{ color: NAVY, mt: 1 }}>
          Advertising Article 26:
        </Typography>
        <P>TifoXR MAY display advertising and sponsored content including images and videos managed via TifoXR CMS.</P>
        <P>
          All ads show to everyone - no discrimination, no targeting by user data, no profiling, same ad to all
          users in space. All ads clearly labeled as advertising/sponsored with sponsor/advertiser identified.
        </P>
        <P>
          Users may report ads via{' '}
          <Link href="mailto:compliance@globalmobisoft.com" sx={{ fontWeight: 700 }}>compliance@globalmobisoft.com</Link>.
        </P>
        <P>
          No dark patterns Article 25. No targeted advertising to minors Article 28, no sensitive data used, no
          profiling-based ads.
        </P>
      </Section>

      <Section number={7} title="Authority Orders and Transparency (Articles 9, 10, 15, 24 DSA)">
        <P>We act on EU/UK/US authority orders per Articles 9-10, inform user unless prohibited.</P>
        <P>
          Annual transparency report per Articles 15 and 24: number of Article 16 notices by type, actions, median
          time, removals/suspensions by basis, appeals/overturn rate, automated tool accuracy/error, authority
          orders by state, team size/training/location.
        </P>
        <P>First report 12 months after go-live at globalmobisoft.com/dsa#transparency.</P>
        <P>SoR submitted to EU DSA Transparency Database per 2024/2835.</P>
        <P>Average Monthly Active Recipients tracked per Article 24(2).</P>
      </Section>

      <Section number={8} title="Additional Information">
        <P>Required by Regulation (EU) 2022/2065. Fine up to 6% turnover Article 74.</P>
        <P>
          Privacy{' '}
          <Link href="https://www.globalmobisoft.com/privacy" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 700 }}>
            https://www.globalmobisoft.com/privacy
          </Link>
          , Terms{' '}
          <Link href="https://www.globalmobisoft.com/terms" target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 700 }}>
            https://www.globalmobisoft.com/terms
          </Link>
          .
        </P>
        <P>
          Contact:{' '}
          <Link href="mailto:compliance@globalmobisoft.com" sx={{ fontWeight: 700 }}>compliance@globalmobisoft.com</Link>
          {' '}and{' '}
          <Link href="mailto:tifoxr@datarep.com" sx={{ fontWeight: 700 }}>tifoxr@datarep.com</Link>
        </P>
      </Section>

      <Divider sx={{ my: 1 }} />

      <Typography variant="caption" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
        Ref: GMS-DSA-2026-05-FINAL-V4 | GMS 7014 13th Ave Suite 202 Brooklyn NY 11228 | EU Rep DataRep Dublin D02
        XE80 + London EC4A 2AB |{' '}
        <Link href="mailto:tifoxr@datarep.com" sx={{ fontWeight: 700, color: '#94a3b8' }}>tifoxr@datarep.com</Link>
        {' '}| Hive.ai TEXT ONLY, Ads ALL USERS same, Chat chronological latest at bottom
      </Typography>
    </LegalPageLayout>
  );
}
